/**
 * DON!! cards, held in the browser.
 *
 * The catalogue bundled with the app is every card that has a number printed
 * on it. DON!! cards do not have one, so they are not in it, and were not in
 * the price source this app asked either — which meant a $722 card the app
 * could not find, price, or put in a collection.
 *
 * Kept alongside the set names rather than bundled with the catalogue, for the
 * same reason those are: 187 rows carrying live prices do not belong in a
 * build artifact that only changes when the app is deployed. One request, held
 * for a day, and the search works without it — it simply finds no DON cards,
 * which is exactly what it did before.
 */

const KEY = "ppt_op_don_v1";
/* A day. These carry prices, so they go stale in a way set names do not — but
   a price a day old on a card nobody else can price at all is still the best
   answer available, and the alternative is a request per visit. */
const TTL_MS = 24 * 60 * 60 * 1000;

let memo = null;

const read = () => {
  try {
    const raw = window.localStorage.getItem(KEY);
    const held = raw ? JSON.parse(raw) : null;
    if (held && Array.isArray(held.cards) && typeof held.at === "number") return held;
  } catch {
    /* a corrupt copy is no copy */
  }
  return null;
};

/** Whatever is known right now, without waiting. `[]` until the first load. */
export function knownDon() {
  if (memo) return memo;
  memo = read()?.cards || [];
  return memo;
}

/**
 * Make sure the DON cards are here, fetching only if missing or old.
 *
 * Never throws and never leaves the caller waiting on a failure. Every other
 * kind of One Piece search works without these, so a source that is down costs
 * one kind of card rather than the screen.
 */
export async function loadDon() {
  const held = read();
  if (held && Date.now() - held.at < TTL_MS) {
    memo = held.cards;
    return memo;
  }

  try {
    const res = await fetch("/api/op/don");
    if (!res.ok) throw new Error(String(res.status));
    const { cards } = await res.json();
    if (Array.isArray(cards) && cards.length) {
      memo = cards;
      try {
        window.localStorage.setItem(KEY, JSON.stringify({ at: Date.now(), cards }));
      } catch {
        /* a blocked store means fetching again next visit, not failing now */
      }
      return memo;
    }
  } catch {
    /* fall through to whatever is already held */
  }

  memo = held?.cards || [];
  return memo;
}

/**
 * DON cards matching a query.
 *
 * Deliberately generous about the word "don" itself. Every one of these cards
 * is called "DON!! Card (Someone)", so a bare "don" should list them all, and
 * "don zoro" should find the gold Zoro without anyone having to know that the
 * card's actual name has two exclamation marks in it.
 *
 * The set is matched too, because "don op14" is how somebody with the box in
 * front of them would ask.
 */
export function searchDon(query, cards = knownDon()) {
  const q = String(query || "").toLowerCase().trim();
  if (q.length < 2) return [];

  const words = q.split(/[^a-z0-9]+/).filter(Boolean);
  if (!words.length) return [];

  return cards.filter((c) => {
    const hay = `${c.label || ""} ${c.name || ""} ${c.set || ""}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}
